import { useState } from 'react'
import { useArtefactTemplateMutations, useArtefactTemplates, useCaseSettings } from '../../../hooks'
import type {
  EffectiveCaseSettings,
  UpdateCaseSettingsRequest,
} from '../../../types/case-container'

type ArtefactTemplatesResult = ReturnType<typeof useArtefactTemplates>

export interface CaseSettingsTabState {
  settings: EffectiveCaseSettings | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  updating: boolean
  templates: ArtefactTemplatesResult['templates']
  templatesLoading: boolean
  refetchTemplates: ArtefactTemplatesResult['refetch']
  deleteCase: (caseId: string, templateId: string) => Promise<boolean>
  editMode: boolean
  formData: UpdateCaseSettingsRequest
  setFormData: React.Dispatch<React.SetStateAction<UpdateCaseSettingsRequest>>
  handleEdit: () => void
  handleSave: () => Promise<void>
  handleCancel: () => void
  isInherited: (fieldName: string) => boolean
}

function buildInitialFormData(settings: EffectiveCaseSettings): UpdateCaseSettingsRequest {
  return {
    timezone: settings.timezone,
    dateFormat: settings.dateFormat,
    timeFormat: settings.timeFormat,
    retentionPeriodDays: settings.retentionPeriodDays,
    endClientReference: settings.endClientReference,
    endClientContactName: settings.endClientContactName,
    endClientContactEmail: settings.endClientContactEmail,
    externalCaseName: settings.externalCaseName,
    evidenceNumberFormat: settings.evidenceNumberFormat,
    velociraptorExportPath: settings.velociraptorExportPath,
    evidenceImportPath: settings.evidenceImportPath,
    exportPath: settings.exportPath,
  }
}

export function useCaseSettingsTab(caseId: string): CaseSettingsTabState {
  const { settings, loading, error, refetch, update, updating } = useCaseSettings(caseId)
  const {
    templates,
    loading: templatesLoading,
    refetch: refetchTemplates,
  } = useArtefactTemplates(caseId)
  const { deleteCase } = useArtefactTemplateMutations()
  const [editMode, setEditMode] = useState(false)
  const [formData, setFormData] = useState<UpdateCaseSettingsRequest>({})

  const handleEdit = (): void => {
    if (!settings) return
    setFormData(buildInitialFormData(settings))
    setEditMode(true)
  }

  const handleSave = async (): Promise<void> => {
    const result = await update(formData)
    if (result) setEditMode(false)
  }

  const handleCancel = (): void => {
    setEditMode(false)
    setFormData({})
  }

  const isInherited = (fieldName: string): boolean =>
    settings?.inheritedFields.includes(fieldName) ?? false

  return {
    settings,
    loading,
    error,
    refetch,
    updating,
    templates,
    templatesLoading,
    refetchTemplates,
    deleteCase,
    editMode,
    formData,
    setFormData,
    handleEdit,
    handleSave,
    handleCancel,
    isInherited,
  }
}
